import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Input,
  signal,
  ViewChild,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { Router, RouterModule, NavigationEnd } from '@angular/router'
import { GridComponent } from '@progress/kendo-angular-grid'
import { PDFExportComponent } from '@progress/kendo-angular-pdf-export'
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  Table,
  Footer,
  AlignmentType,
  BorderStyle,
  TableRow,
  TableCell,
  WidthType,
  Header,
  ImageRun,
  ShadingType,
} from 'docx'
import {
  LineChart,
  SunburstChart,
  PieChart,
  HeatmapChart,
} from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  GridComponent as eGridComponent,
  LegendComponent,
  GraphicComponent,
  VisualMapComponent,
} from 'echarts/components'
import * as echarts from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { saveAs } from 'file-saver'
import _ from 'lodash-es'
import { filter, map, startWith } from 'rxjs/operators'

import { Enums, Hunt, HuntCoverage } from '@cyborg-monorepo/hunter/common'
import {
  BadgeComponent,
  BreadcrumbListComponent,
  ButtonComponent,
  ButtonMenuComponent,
  DialogRef,
  DialogService,
  DropdownMenuItemComponent,
  ErrorComponent,
  SegmentedProgressBarComponent,
  TagComponent,
  TypographyComponent,
} from '@hyperkit'

import {
  BreadcrumbService,
  HuntEditorService,
  HuntViewService,
  NavigationService,
} from '@hunt/services'
import { SessionService } from '@hunt/services'

import { addNewLinesBetweenWords } from '../../../helpers/utils'
import {
  TrackingEventEnum,
  TrackingService,
} from '../../../tracking/tracking.service'
import {
  ChartUtils,
  SunburstData,
} from '../../hunt/overview/dashboard/charts/chart-utils'
import * as sunburstChartJson from '../../hunt/overview/dashboard/charts/sunburst-chart.json'
import * as threatPieChartJson from '../../hunt/overview/dashboard/charts/threat-pie-chart.json'
import { ExportOptionsDialogComponent } from '../../hunt/overview/dashboard/export-options-dialog/export-options-dialog.component'
import { HuntMetricsService } from '../../shared/services/hunt-metrics.service'
import { ArchiveHuntDialogComponent } from '../dialogs/archive-hunt-dialog/archive-hunt-dialog.component'
import { EditHuntDialogComponent } from '../dialogs/edit-hunt-dialog/edit-hunt-dialog.component'
import { ReportSectionComponent } from '../report-section/report-section.component'

import { ReportTextBlockComponent } from './report-text-block/report-text-block.component'
import {
  addMarkdownSection,
  buildActorTableDocx,
  buildImageParagraph,
  buildMitreTableDocx,
  buildThreatTableDocx,
  createHeading,
  getBase64FromImageUrl,
  getChartImageBase64,
  markdownToDocxParagraphs,
  markdownToPdfMake,
  renderContentToDocx,
  renderContentToPdfMake,
} from './report-utils'

echarts.use([
  TitleComponent,
  TooltipComponent,
  eGridComponent,
  LegendComponent,
  VisualMapComponent,
  LineChart,
  CanvasRenderer,
  GraphicComponent,
  SunburstChart,
  PieChart,
  HeatmapChart,
])

@Component({
  standalone: true,
  imports: [
    RouterModule,
    ButtonMenuComponent,
    ReportTextBlockComponent,
    ReportSectionComponent,
    BreadcrumbListComponent,
    DropdownMenuItemComponent,
    TypographyComponent,
    ButtonComponent,
    ErrorComponent,
    BadgeComponent,
    TagComponent,
    SegmentedProgressBarComponent,
  ],
  selector: 'hunt-final-report',
  templateUrl: './hunt-final-report.component.html',
  styleUrls: ['hunt-final-report.component.scss'],
})
export class HuntFinalReportComponent implements AfterViewInit {
  readonly navSvc = inject(NavigationService)
  readonly huntView = inject(HuntViewService)
  readonly huntEditor = inject(HuntEditorService)
  readonly breadcrumb = inject(BreadcrumbService)
  readonly dialogService = inject(DialogService)
  readonly huntMetricsService = inject(HuntMetricsService)
  readonly trackingService = inject(TrackingService)
  readonly cdr = inject(ChangeDetectorRef)
  readonly session = inject(SessionService)
  readonly loaded = computed(
    () => !this.session.loading() && this.session.activeHunt() !== null,
  )
  private readonly router = inject(Router)

  @ViewChild('pdf') public pdf!: PDFExportComponent
  @ViewChild('reportHtml', { static: false }) public reportHtml!: ElementRef
  public reportDate = new Date().toLocaleDateString()

  public actorSunburst: echarts.ECharts
  public actorSunburstOptions = sunburstChartJson
  public mitreSunburst: echarts.ECharts
  public mitreSunburstOptions = sunburstChartJson
  public threatPie: echarts.ECharts
  public threatPieOptions = threatPieChartJson
  public imageUrl: string
  public chartColors = ChartUtils.sunburstColors

  public readonly breadcrumbs = this.breadcrumb.finalReport

  readonly huntPackageIds = computed(() =>
    this.huntView.huntPackages()?.map((pkg) => pkg.UUID),
  )

  //
  // 🆕 Reactive URL watcher
  //
  private readonly urlSignal = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      startWith(null),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  )

  /**
   * returns:
   *  - 'overview'      for /…/final-report/overview
   *  - 'conclusion'    for /…/final-report/conclusion
   *  - '<pkgUUID>'     for /…/final-report/technical-summary/:pkgUUID
   *  - 'coverage'      for /…/final-report/coverage
   */
  readonly selectedSectionId = computed<string>(() => {
    const clean = this.urlSignal().split('?')[0].split('#')[0]
    const parts = clean.split('/').filter(Boolean)
    const idx = parts.indexOf('final-report')
    const sec = parts[idx + 1]
    const pkg = parts[idx + 2]

    if (sec === 'overview') return 'overview'
    if (sec === 'conclusion') return 'conclusion'
    if (sec === 'coverage') return 'coverage'
    if (sec === 'technical-summary' && pkg) return pkg
    return 'overview'
  })

  readonly sections = computed(() => [
    { id: 'overview', title: 'Overview' },
    {
      id: 'technical-summary',
      title: 'Technical Summary',
      children: this.huntView
        .huntPackages()
        .filter((pkg) => {
          return pkg.reportAnalysis?.length > 0
        })
        ?.map((pkg) => ({
          id: pkg.UUID,
          title: pkg.title,
        })),
    },
    { id: 'conclusion', title: 'Conclusion' },
    { id: 'coverage', title: 'Coverage' },
  ])

  async ngAfterViewInit(): Promise<void> {
    try {
      await this.waitForElement('#top-mitre-container')
      await this.waitForElement('#top-actors-container')
      await this.waitForElement('#top-threats-container')

      this.mitreSunburst = echarts.init(
        document.getElementById('top-mitre-container')!,
      )
      this.actorSunburst = echarts.init(
        document.getElementById('top-actors-container')!,
      )
      this.threatPie = echarts.init(
        document.getElementById('top-threats-container')!,
      )

      this.loadChartData()
    } catch (err) {
      console.error('Chart initialization failed:', err)
    }
  }

  // Currently selected report section from the URL
  readonly selectedTab = this.selectedSectionId
  selectedSection: 'overview' | 'hunt-package' | 'conclusion' | 'coverage' =
    'overview'

  exportOptions: string[] = [
    'Overview',
    'Technical Summary',
    'Conclusion',
    'Coverage',
  ]

  public huntActorCoverage: HuntCoverage[]
  public huntMITRECoverage: HuntCoverage[]
  public huntThreatCoverage: HuntCoverage[]
  public error = ''

  readonly selectedHuntPackageId = computed(() => {
    const section = this.selectedSectionId()
    return this.huntPackageIds().includes(section) ? section : null
  })

  onSelectSection(sectionId: string): void {
    const isHuntPackage = this.huntPackageIds()?.includes(sectionId)

    if (isHuntPackage) {
      this.navSvc.navigateToFinalReportTechSummary(sectionId)
    } else if (sectionId === 'overview') {
      this.navSvc.navigateToFinalOverview()
    } else if (sectionId === 'conclusion') {
      this.navSvc.navigateToFinalConclusion()
    } else if (sectionId === 'coverage') {
      this.navSvc.navigateToFinalCoverage()
    }
  }
  private applyInlinePdfStyles = () => {
    const links = this.reportHtml.nativeElement.querySelectorAll('a')
    links.forEach((el: HTMLElement) => {
      el.style.color = '#ff3750'
      el.style.opacity = '1'
      el.style.visibility = 'visible'
      el.style.mixBlendMode = 'normal'
      el.style.isolation = 'auto'
      el.style.textShadow = 'none'
      el.style.filter = 'none'
      el.style.fontWeight = '400'
      el.style.textTransform = 'none'
      el.style.fontFamily = `'Roboto', 'Arial', 'sans-serif'`
      el.style.fontSize = '11pt'
    })

    const nodes = this.reportHtml.nativeElement.querySelectorAll(
      'p, span, li, h1, h2, h3, h4, h5, h6, td, th, pre',
    )
    nodes.forEach((el: HTMLElement) => {
      el.style.color = '#ffffff'
      el.style.opacity = '1'
      el.style.visibility = 'visible'
      el.style.mixBlendMode = 'normal'
      el.style.isolation = 'auto'
      el.style.textShadow = 'none'
      el.style.filter = 'none'
      el.style.fontWeight = '400'
      el.style.textTransform = 'none'
      el.style.fontFamily = `'Roboto', 'Arial', 'sans-serif'`
      el.style.fontSize = '11pt'
    })
  }

  public exportToPDF(grid: GridComponent): void {
    grid.saveAsPDF()
  }

  // This function generates the full HTML string to convert
  generateReportFromHTML(htmlSections: string[]): string {
    return `
    <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; font-size: 12pt; }
          h1 { font-size: 20pt; }
          h2 { font-size: 16pt; }
          p { margin: 10px 0; }
        </style>
      </head>
      <body>
      <p style="text-align:center">Report generated by Intel 471</p>
        ${htmlSections.join('')}
      </body>
    </html>
  `
  }

  public createCoverageSunburst(results: HuntCoverage[]): SunburstData[] {
    const sunburstData: SunburstData[] = []
    const country_set: Set<string> = new Set()
    let count = 0
    for (const result of results) {
      if (!country_set.has(result.category)) {
        const children: SunburstData[] = []
        count++
        children.push({
          itemStyle: { color: `${this.chartColors[count]}` },
          name: addNewLinesBetweenWords(result.name),
          value: result.huntPackageCount,
        })
        sunburstData.push({
          itemStyle: { color: `${this.chartColors[count]}` },
          name: addNewLinesBetweenWords(result.category),
          children,
        })
        country_set.add(result.category)
      } else {
        const targetCategory = sunburstData.find(
          (data) => data.name == addNewLinesBetweenWords(result.category),
        )
        targetCategory.children.push({
          itemStyle: { color: `${targetCategory.itemStyle.color}` },
          name: addNewLinesBetweenWords(result.name),
          value: result.huntPackageCount,
        })
      }
    }
    return sunburstData
  }

  public updateSelectedSection(
    section: 'overview' | 'hunt-package' | 'conclusion' | 'coverage',
  ): void {
    this.selectedSection = section
    this.cdr.detectChanges()
  }

  public scrollTo(target: string): void {
    document.getElementById(target).scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    })
  }

  public validateReportString(reportString: string): boolean {
    return reportString && reportString != '<p></p>' && reportString != ''
  }

  /** Open the “Edit Hunt” dialog */
  async editHunt() {
    const hunt = this.session.activeHunt()
    if (!hunt) return
    const ref = this.dialogService.open<Hunt>(EditHuntDialogComponent, {
      data: hunt,
    })
    ref.afterClosed().subscribe(async (hunt: Hunt | undefined) => {
      if (hunt) {
        await this.session.loadHunt((hunt as Hunt).id, true)
      }
    })
  }

  /** Archive the current hunt */
  async archiveHunt() {
    const hunt = this.session.activeHunt()
    if (!hunt) return
    const ref = this.dialogService.open<Hunt>(ArchiveHuntDialogComponent, {
      data: hunt,
    })

    ref.afterClosed().subscribe(async (archived: Hunt | undefined) => {
      if (archived) {
        await this.session.loadHunt(archived.id, true)
        this.router.navigate(['/hunt/hunts'])
      }
    })
  }

  /** Handle the dropdown menu actions */
  handleMenuAction(event: { value: 'update' | 'archive' }) {
    if (event.value === 'update') this.editHunt()
    else if (event.value === 'archive') this.archiveHunt()
  }

  public async reopenHunt(): Promise<void> {
    await this.huntEditor.updateStatus(Enums.StatusEnum.IN_PROGRESS)
    await this.huntEditor.updateEditing(false)
    await this.session.loadHunt(this.navSvc.huntId())
    this.navSvc.navigateToWorkbench()
  }

  async exportPdf() {
    const dialogRef: DialogRef = this.dialogService.open(
      ExportOptionsDialogComponent,
      {
        data: this.exportOptions,
      },
    )

    dialogRef.afterClosed().subscribe(async (exportOptions) => {
      if (!exportOptions) return

      const { actorImg, mitreImg, threatImg } = this.getChartImages()

      const sections = exportOptions as string[]
      const pdfMake = (await import('pdfmake/build/pdfmake')).default
      const pdfFonts: any = await import('pdfmake/build/vfs_fonts')
      pdfMake.vfs = pdfFonts.default ?? pdfFonts

      const hunt = this.huntView
      const huntPackages = this.huntView.huntPackages()
      const hasCoverage = this.huntView
        .huntPackages()
        .some((pkg) => pkg.reportAnalysis?.length > 0)

      const currentDate = new Date().toLocaleDateString()

      const docDefinition: any = {
        content: [],
        pageMargins: [40, 60, 40, 60],
        styles: {
          title: {
            fontSize: 22,
            bold: true,
            alignment: 'left',
            margin: [0, 20, 0, 10],
          },
          header: { fontSize: 18, bold: true, margin: [0, 10, 0, 5] },
          subheader: { fontSize: 14, bold: true, margin: [0, 10, 0, 5] },
          text: { fontSize: 10, margin: [0, 5, 0, 5] },
          tableHeader: { bold: true, fillColor: '#eee' },
        },
        footer: function (currentPage: number, pageCount: number) {
          return {
            margin: [40, 10, 40, 0],
            columns: [
              {
                text: '© Intel 471 Inc. All rights reserved',
                alignment: 'left',
                fontSize: 9,
              },
              {
                text: `${currentPage} of ${pageCount}`,
                alignment: 'right',
                fontSize: 9,
              },
            ],
          }
        },
      }
      const logoBase64 = await getBase64FromImageUrl(
        '/assets/brand/report-logo.png',
      )

      docDefinition.header = function () {
        return {
          columns: [
            {
              image: logoBase64,
              width: 80,
              margin: [40, 10, 0, 10],
            },
          ],
        }
      }

      docDefinition.content.push(
        { text: `Hunt Report: ${hunt.name()}`, style: 'title' },
        {
          text: `${this.huntView.assigneeFullName()} ${currentDate}`,
          style: 'subheader',
        },
      )
      if (sections?.includes('Overview')) {
        if (hunt.executiveSummary()) {
          addMarkdownSection(
            'Executive Summary',
            hunt.executiveSummary(),
            docDefinition,
          )
        }

        if (hunt.abstract()) {
          addMarkdownSection('Abstract', hunt.abstract(), docDefinition)
        }

        if (hunt.hypothesis()) {
          addMarkdownSection('Hypothesis', hunt.hypothesis(), docDefinition)
        }
      }

      if (sections?.includes('Technical Summary') && hasCoverage) {
        docDefinition.content.push({
          text: 'Technical Summary',
          style: 'header',
        })

        if (hunt.outcome()) {
          docDefinition.content.push(
            {
              text: `Outcome`,
              style: 'subheader',
            },
            { text: `${hunt.outcome()}`, style: 'text' },
          )
        }

        if (hunt.outcomeDependent()) {
          docDefinition.content.push({
            text: `${hunt.outcomeDependentField()}`,
            style: 'subheader',
          })

          const pdfContent = markdownToPdfMake(hunt.outcomeDependent())
          if (Array.isArray(pdfContent)) {
            docDefinition.content.push(...pdfContent)
          } else {
            docDefinition.content.push(pdfContent)
          }
        }

        for (const huntPack of huntPackages) {
          if (!huntPack.reportAnalysis?.length) continue

          docDefinition.content.push({
            text: huntPack.title,
            style: 'subheader',
          })

          if (huntPack.description) {
            docDefinition.content.push({
              text: 'Threat Description',
              style: 'text',
            })

            const descContent = markdownToPdfMake(huntPack.description)
            if (Array.isArray(descContent)) {
              docDefinition.content.push(...descContent)
            } else {
              docDefinition.content.push(descContent)
            }
          }

          const mitre = huntPack.mapping?.mitre
          if (
            mitre?.mitre_attack_tactics?.length ||
            mitre?.mitre_attack_techniques?.length
          ) {
            docDefinition.content.push({
              text: 'MITRE Information',
              style: 'subheader',
            })

            const idsTextObjects =
              mitre?.mitre_attack_technique_ids?.map((id, index, arr) => ({
                text: id + (index < arr?.length - 1 ? ', ' : ''),
                link: `https://attack.mitre.org/techniques/${id.replace('.', '/')}`,
                color: 'blue',
              })) || []
            docDefinition.content.push({
              columns: [
                { text: `Tactics: ${mitre?.mitre_attack_tactics.join(', ')}` },
                {
                  text: `Techniques: ${mitre?.mitre_attack_techniques.join(', ')}`,
                },
              ],
            })
            if (idsTextObjects?.length) {
              docDefinition.content.push({
                text: [
                  { text: 'Technique IDs: ', bold: true },
                  ...idsTextObjects,
                ],
                style: 'text',
              })
            }
          }

          for (const environment of huntPack.reportAnalysis) {
            for (const tool of environment.tools) {
              for (const content of tool.generatedContent.Hunt) {
                renderContentToPdfMake(
                  content,
                  `${environment.name}: ${tool.name}`,
                  docDefinition.content,
                )
              }
            }
          }
        }
      }

      if (sections?.includes('Conclusion') && hunt.conclusion()) {
        addMarkdownSection('Conclusion', hunt.conclusion(), docDefinition)
      }

      if (sections?.includes('Coverage') && hasCoverage) {
        docDefinition.content.push({
          text: 'Coverage',
          style: 'header',
          pageBreak: 'before',
        })

        const mitreTableBody = [
          // Header row
          [
            { text: 'Technique', style: 'tableHeader' },
            { text: 'Tactic', style: 'tableHeader' },
            { text: 'Packages', style: 'tableHeader' },
          ],
          // Data rows
          ...this.huntMITRECoverage?.map((mitre) => [
            {
              text: mitre.name,
              color: 'blue',
              link: `https://attack.mitre.org/techniques/${mitre.name.replace('.', '/')}`,
              style: 'text',
            },
            { text: mitre.category, style: 'text' },
            { text: mitre.huntPackageCount.toString(), style: 'text' },
          ]),
        ]

        const actorTableBody = [
          [
            { text: 'Name', style: 'tableHeader' },
            { text: 'Origin', style: 'tableHeader' },
            { text: 'Packages', style: 'tableHeader' },
          ],
          ...this.huntActorCoverage?.map((actor) => [
            { text: actor.name, style: 'text' },
            { text: actor.category, style: 'text' },
            { text: actor.huntPackageCount.toString(), style: 'text' },
          ]),
        ]
        docDefinition.content.push({
          text: 'MITRE Coverage',
          style: 'header',
        })

        if (mitreImg) {
          docDefinition.content.push({
            image: mitreImg,
            width: 400,
            alignment: 'center',
            margin: [0, 10, 0, 10],
          })
        }

        docDefinition.content.push({
          table: {
            headerRows: 1,
            widths: ['*', '*', '*'],
            body: mitreTableBody,
          },
          layout: 'lightHorizontalLines',
        })

        const threatTableBody = [
          [
            { text: 'Name', style: 'tableHeader' },
            { text: 'Packages', style: 'tableHeader' },
          ],
          ...this.huntThreatCoverage?.map((threat) => [
            { text: threat.name, style: 'text' },
            { text: threat.huntPackageCount.toString(), style: 'text' },
          ]),
        ]

        docDefinition.content.push({
          text: 'Actor Coverage',
          style: 'header',
          pageBreak: 'before',
        })

        if (actorImg) {
          docDefinition.content.push({
            image: actorImg,
            width: 400,
            alignment: 'center',
            margin: [0, 10, 0, 10],
          })
        }

        docDefinition.content.push({
          table: {
            headerRows: 1,
            widths: ['*', '*', '*'],
            body: actorTableBody,
          },
          layout: 'lightHorizontalLines',
        })

        docDefinition.content.push({
          text: 'Threat Coverage',
          style: 'header',
          pageBreak: 'before',
        })
        if (threatImg) {
          docDefinition.content.push({
            image: threatImg,
            width: 400,
            alignment: 'center',
            margin: [0, 10, 0, 10],
          })
        }

        docDefinition.content.push({
          table: {
            headerRows: 1,
            widths: ['70%', '30%'],
            body: threatTableBody,
          },
          layout: 'lightHorizontalLines',
        })
      }
      pdfMake
        .createPdf(docDefinition)
        .download(`Hunt_Report_${hunt.name()}.pdf`)
      this.trackingService.trackEvent(TrackingEventEnum.HUNT_REPORT_EXPORTED)
    })
  }

  public async exportWord(): Promise<void> {
    const dialogRef: DialogRef = this.dialogService.open(
      ExportOptionsDialogComponent,
      {
        data: this.exportOptions,
      },
    )

    dialogRef.afterClosed().subscribe(async (exportOptions) => {
      if (!exportOptions) return

      const sections = exportOptions as string[] // Get selected sections
      const { actorImg, mitreImg, threatImg } = this.getChartImages()
      const title = this.huntView.name()
      const date = this.reportDate
      const hunt = this.huntView
      const huntPackages = hunt.huntPackages()
      const hasCoverage = huntPackages.some(
        (pkg) => pkg.reportAnalysis?.length > 0,
      )

      const docParagraphs: (Paragraph | Table)[] = []

      // Title
      docParagraphs.push(...markdownToDocxParagraphs(`# ${title}`))
      docParagraphs.push(
        ...markdownToDocxParagraphs(`_${hunt.assigneeFullName()} ${date}_`),
      )

      // Overview Section: Executive Summary, Abstract, Hypothesis
      if (sections.includes('Overview')) {
        const mdSections = [
          { label: 'Executive Summary', content: hunt.executiveSummary() },
          { label: 'Abstract', content: hunt.abstract() },
          { label: 'Hypothesis', content: hunt.hypothesis() },
        ]

        for (const sec of mdSections) {
          if (sec.content) {
            const contentParagraphs = markdownToDocxParagraphs(sec.content)
            docParagraphs.push(createHeading(sec.label, HeadingLevel.HEADING_2))
            docParagraphs.push(...contentParagraphs)
          }
        }
      }

      // Technical Summary Section
      if (sections.includes('Technical Summary') && hasCoverage) {
        docParagraphs.push(
          createHeading('Technical Summary', HeadingLevel.HEADING_2),
        )

        if (hunt.outcome()) {
          docParagraphs.push(
            createHeading('Outcome', HeadingLevel.HEADING_3, 0),
          )

          docParagraphs.push(...markdownToDocxParagraphs(hunt.outcome()))
        }

        if (hunt.outcomeDependent()) {
          docParagraphs.push(
            createHeading(
              hunt.outcomeDependentField(),
              HeadingLevel.HEADING_3,
              0,
            ),
          )

          docParagraphs.push(
            ...markdownToDocxParagraphs(hunt.outcomeDependent()),
          )
        }

        for (const huntPack of huntPackages) {
          if (!huntPack.reportAnalysis?.length) continue
          docParagraphs.push(
            createHeading(huntPack.title, HeadingLevel.HEADING_3, 100),
          )

          if (huntPack.description) {
            docParagraphs.push(
              createHeading('Threat Description', HeadingLevel.HEADING_4, 0),
            )

            docParagraphs.push(
              ...markdownToDocxParagraphs(huntPack.description),
            )
          }

          const mitre = huntPack.mapping?.mitre
          if (
            mitre?.mitre_attack_tactics?.length ||
            mitre?.mitre_attack_techniques?.length ||
            mitre?.mitre_attack_technique_ids?.length
          ) {
            docParagraphs.push(
              createHeading('MITRE Information', HeadingLevel.HEADING_5, 0),
            )

            if (mitre.mitre_attack_tactics?.length) {
              docParagraphs.push(
                ...markdownToDocxParagraphs(
                  `**Tactics:** ${mitre.mitre_attack_tactics.join(', ')}`,
                ),
              )
            }
            if (mitre.mitre_attack_techniques?.length) {
              docParagraphs.push(
                ...markdownToDocxParagraphs(
                  `**Techniques:** ${mitre.mitre_attack_techniques.join(', ')}`,
                ),
              )
            }
            if (mitre.mitre_attack_technique_ids?.length) {
              const techniqueLinks = mitre.mitre_attack_technique_ids
                .map(
                  (id) =>
                    `[${id}](https://attack.mitre.org/techniques/${id.replace('.', '/')})`,
                )
                .join(', ')
              docParagraphs.push(
                ...markdownToDocxParagraphs(
                  `**Technique IDs:** ${techniqueLinks}`,
                ),
              )
            }
          }

          for (const environment of huntPack.reportAnalysis) {
            for (const tool of environment.tools) {
              const huntContent = tool.generatedContent?.Hunt
              if (!huntContent?.length) continue

              for (const content of huntContent) {
                const renderedParagraphs = renderContentToDocx(
                  content,
                  `${environment.name}: ${tool.name}`,
                )
                docParagraphs.push(...renderedParagraphs)
              }
            }
          }
        }
      }

      // Conclusion Section
      if (sections.includes('Conclusion') && hunt.conclusion()) {
        docParagraphs.push(createHeading('Conclusion', HeadingLevel.HEADING_2))
        docParagraphs.push(...markdownToDocxParagraphs(hunt.conclusion()))
        docParagraphs.push(new Paragraph({ spacing: { before: 300 } }))
      }

      // Coverage Section
      if (sections.includes('Coverage') && hasCoverage) {
        docParagraphs.push(createHeading('Coverage', HeadingLevel.HEADING_2))

        // MITRE Coverage
        if (mitreImg || this.huntMITRECoverage?.length > 0) {
          docParagraphs.push(
            createHeading('MITRE Coverage', HeadingLevel.HEADING_3),
          )
          if (mitreImg) docParagraphs.push(buildImageParagraph(mitreImg))
          if (this.huntMITRECoverage?.length > 0) {
            docParagraphs.push(new Paragraph(''))
            docParagraphs.push(buildMitreTableDocx(this.huntMITRECoverage))
          }
        }

        // Actor Coverage
        if (actorImg || this.huntActorCoverage?.length > 0) {
          docParagraphs.push(
            createHeading('Actor Coverage', HeadingLevel.HEADING_3, 100),
          )

          if (actorImg) docParagraphs.push(buildImageParagraph(actorImg))
          if (this.huntActorCoverage?.length > 0) {
            docParagraphs.push(new Paragraph(''))
            docParagraphs.push(buildActorTableDocx(this.huntActorCoverage))
          }
        }

        // Threat Coverage
        if (threatImg || this.huntThreatCoverage?.length > 0) {
          docParagraphs.push(
            createHeading('Threat Coverage', HeadingLevel.HEADING_3, 100),
          )
          if (threatImg) docParagraphs.push(buildImageParagraph(threatImg))
          if (this.huntThreatCoverage?.length > 0) {
            docParagraphs.push(new Paragraph(''))
            docParagraphs.push(buildThreatTableDocx(this.huntThreatCoverage))
          }
        }
      }

      const response = await fetch('/assets/brand/report-logo.png')
      const arrayBuffer = await response.arrayBuffer()
      const imageBuffer = new Uint8Array(arrayBuffer)

      // Finalize doc
      const doc = new Document({
        creator: this.huntView.assigneeFullName?.() || 'Hunt Exporter',
        title: this.huntView.name(),
        description: `Hunt Report exported on ${this.reportDate}`,
        styles: {
          default: {
            document: {
              run: {
                font: 'Lato', // default for body
                size: 22,
              },
            },
          },
          paragraphStyles: [
            {
              id: 'Heading1',
              name: 'Heading 1',
              basedOn: 'Normal',
              next: 'Normal',
              quickFormat: true,
              run: {
                font: 'Proxima Nova',
                bold: true,
                size: 32,
              },
              paragraph: {
                spacing: {
                  after: 240,
                },
              },
            },
            {
              id: 'Heading2',
              name: 'Heading 2',
              basedOn: 'Normal',
              next: 'Normal',
              quickFormat: true,
              run: {
                font: 'Proxima Nova',
                bold: true,
                size: 28,
              },
              paragraph: {
                spacing: {
                  after: 200,
                },
              },
            },
            {
              id: 'Heading3',
              name: 'Heading 3',
              basedOn: 'Normal',
              next: 'Normal',
              quickFormat: true,
              run: {
                font: 'Proxima Nova',
                bold: true,
                size: 26,
                color: '000000',
              },
              paragraph: {
                spacing: { after: 160 },
              },
            },
            {
              id: 'Heading4',
              name: 'Heading 4',
              basedOn: 'Normal',
              next: 'Normal',
              quickFormat: true,
              run: {
                font: 'Proxima Nova',
                bold: true,
                size: 24,
                color: '000000',
              },
              paragraph: {
                spacing: { after: 120 },
              },
            },
            {
              id: 'Heading5',
              name: 'Heading 5',
              basedOn: 'Normal',
              next: 'Normal',
              quickFormat: true,
              run: {
                font: 'Proxima Nova',
                bold: true,
                size: 22,
                color: '000000',
              },
              paragraph: {
                spacing: { after: 100 },
              },
            },
          ],
        },
        sections: [
          {
            properties: {
              page: {
                margin: {
                  bottom: 1440,
                  left: 1440,
                  right: 1440,
                  header: 720,
                  footer: 720,
                },
              },
            },
            headers: {
              default: new Header({
                children: [
                  new Paragraph({
                    children: [],
                    shading: {
                      fill: 'FFFFFF',
                      type: ShadingType.CLEAR,
                      color: 'auto',
                    },
                  }),
                  new Table({
                    width: { size: 100, type: WidthType.PERCENTAGE },
                    borders: {
                      top: {
                        style: BorderStyle.NONE,
                        size: 0,
                        color: 'FFFFFF',
                      },
                      bottom: {
                        style: BorderStyle.NONE,
                        size: 0,
                        color: 'FFFFFF',
                      },
                      left: {
                        style: BorderStyle.NONE,
                        size: 0,
                        color: 'FFFFFF',
                      },
                      right: {
                        style: BorderStyle.NONE,
                        size: 0,
                        color: 'FFFFFF',
                      },
                      insideHorizontal: {
                        style: BorderStyle.NONE,
                        size: 0,
                        color: 'FFFFFF',
                      },
                      insideVertical: {
                        style: BorderStyle.NONE,
                        size: 0,
                        color: 'FFFFFF',
                      },
                    },
                    rows: [
                      new TableRow({
                        children: [
                          new TableCell({
                            children: [
                              new Paragraph({
                                alignment: AlignmentType.LEFT,
                                children: [
                                  new ImageRun({
                                    data: imageBuffer,
                                    type: 'png',
                                    transformation: {
                                      width: 90,
                                      height: 30,
                                    },
                                  }),
                                ],
                              }),
                            ],
                            shading: {
                              fill: 'FFFFFF',
                              type: ShadingType.CLEAR,
                              color: 'auto',
                            },
                            borders: {
                              top: { style: BorderStyle.NONE },
                              bottom: { style: BorderStyle.NONE },
                              left: { style: BorderStyle.NONE },
                              right: { style: BorderStyle.NONE },
                            },
                          }),
                        ],
                      }),
                      new TableRow({
                        children: [
                          new TableCell({
                            columnSpan: 2,
                            children: [
                              new Paragraph({
                                border: {
                                  bottom: {
                                    color: 'A0A0A0',
                                    space: 1,
                                    style: BorderStyle.NONE,
                                    size: 6,
                                  },
                                },
                              }),
                            ],
                            shading: {
                              fill: 'FFFFFF',
                              type: ShadingType.CLEAR,
                              color: 'auto',
                            },
                            borders: {
                              top: { style: BorderStyle.NONE },
                              bottom: { style: BorderStyle.NONE },
                              left: { style: BorderStyle.NONE },
                              right: { style: BorderStyle.NONE },
                            },
                          }),
                        ],
                      }),
                    ],
                  }),
                ],
              }),
            },
            footers: {
              default: new Footer({
                children: [
                  new Paragraph({
                    alignment: AlignmentType.CENTER,
                    children: [
                      new TextRun({
                        text: '© Intel 471 Inc. All rights reserved.',
                        size: 20,
                        color: '000000',
                      }),
                    ],
                  }),
                ],
              }),
            },

            children: [
              new Paragraph({
                children: [new TextRun('')],
              }),

              ...docParagraphs,
            ],
          },
        ],
      })

      const blob = await Packer.toBlob(doc)
      saveAs(blob, `${title.split(' ').join('-')}-${date}.docx`)
      this.trackingService.trackEvent(TrackingEventEnum.HUNT_REPORT_EXPORTED)
    })
  }

  private loadChartData(): void {
    this.huntMetricsService
      .getHuntThreatCoverageById(this.navSvc.huntId())
      .subscribe((results) => {
        this.huntThreatCoverage = results.results

        const threatChartData = results.results?.map(
          ({ name, huntPackageCount }, index) => ({
            value: Number(huntPackageCount),
            name: addNewLinesBetweenWords(name),
            itemStyle: { color: this.chartColors[index] },
          }),
        )
        this.threatPieOptions.series[0].data = threatChartData
        this.threatPie.setOption(this.threatPieOptions)
      })

    this.huntMetricsService
      .getHuntMITRECoverageById(this.navSvc.huntId())
      .subscribe((results) => {
        this.huntMITRECoverage = results.results
        const sunburstResults: SunburstData[] = this.createCoverageSunburst(
          results.results,
        )
        this.mitreSunburstOptions.series.data = sunburstResults
        this.mitreSunburst.setOption(this.mitreSunburstOptions)
      })

    this.huntMetricsService
      .getHuntActorCoverageById(this.navSvc.huntId())
      .subscribe((results) => {
        this.huntActorCoverage = results.results
        const sunburstResults: SunburstData[] = this.createCoverageSunburst(
          results.results,
        )
        this.actorSunburstOptions.series.data = sunburstResults
        this.actorSunburst.setOption(this.actorSunburstOptions)
      })
  }

  private getChartImages() {
    return {
      actorImg: getChartImageBase64(this.actorSunburst),
      mitreImg: getChartImageBase64(this.mitreSunburst),
      threatImg: getChartImageBase64(this.threatPie),
    }
  }

  private async initChartsIfNeeded(): Promise<void> {
    await this.waitForElement('#top-actors-container')
    await this.waitForElement('#top-mitre-container')
    await this.waitForElement('#top-threats-container')

    if (!this.actorSunburst || !this.mitreSunburst || !this.threatPie) {
      this.actorSunburst = echarts.init(
        document.getElementById('top-actors-container')!,
      )
      this.mitreSunburst = echarts.init(
        document.getElementById('top-mitre-container')!,
      )
      this.threatPie = echarts.init(
        document.getElementById('top-threats-container')!,
      )
      this.loadChartData()

      // Wait for ECharts to render fully
      await new Promise((resolve) => setTimeout(resolve, 150))
    }
  }

  private async waitForElement(selector: string): Promise<void> {
    const maxTries = 20
    let tries = 0

    return new Promise((resolve, reject) => {
      const check = () => {
        const el = document.querySelector(selector)
        if (el) {
          resolve()
        } else if (++tries >= maxTries) {
          reject(`Element not found: ${selector}`)
        } else {
          setTimeout(check, 50)
        }
      }
      check()
    })
  }
}
